import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import { pick } from 'ramda'
import type React from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import { listTablePrefs, type TablePrefsParams } from 'app/constants'
import { canManageInfrastructure } from 'app/core/utils/rbac'
import GenerateSupportBundleModal from 'app/plugins/openstack/components/infrastructure/hosts/GenerateSupportBundleModal'
import { type RootState, useAppSelector } from 'app/store'
import type { ArrayElement } from 'core/actions/Action'
import type InferActionParams from 'core/actions/InferActionParams'
import DocumentMeta from 'core/components/DocumentMeta'
import PollingData, { POLLING_INTERVALS } from 'core/components/PollingData'
import SimpleLink from 'core/components/SimpleLink'
import TitleWithInfoIcon from 'core/components/TitleWithInfoIcon'
import ListContainer from 'core/containers/ListContainer'
import BadgeWithIcon from 'core/elements/badge/BadgeWithIcon'
import type { GridViewColumn } from 'core/elements/grid/Grid'
import type { SortingState } from 'core/elements/grid/hooks/useGridSorting'
import Text from 'core/elements/Text'
import ToggleSwitch from 'core/elements/ToggleSwitch'
import useListAction from 'core/hooks/useListAction'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import useUrlFilterParams from 'core/hooks/useUrlFilterParams'
import { type SessionState, sessionActions, sessionStoreKey } from 'core/session/sessionReducers'
import type Theme from 'core/themes/model'
import { routeManager } from 'core/utils/routes'
import UsageProgress from 'k8s/components/capacity-and-health/UsageProgress'
import type DataKeys from 'k8s/DataKeys'
import { formatTimestampToLocal } from '../../vms/helpers'
import { listBlueprints, listHostAggregates, listResmgrHosts } from '../actions'
import { usagePercentSort } from '../hosts/helpers'
import { hostAggregatesSelector } from '../selectors'
import AddHostToClusterModal from './AddHostToClusterModal'
import {
  deleteClusterHostGroup,
  listClusterHostGroups,
  listClusterHostGroupsAllTenants,
  listClusterVMHAStatus,
} from './actions'
import ClusterAccessCell from './ClusterAccessCell'
import CreateClusterHostGroupsModal from './CreateClusterHostGroupsModal'
import DeleteClusterHostGroupsDialog from './DeleteClusterHostGroupsDialog'
import EditClusterHostGroupModal from './EditClusterHostGroupModal'
import StretchedClustersCellComponent from './StretchedClustersCellComponent'
import { clusterHostGroupsAllTenantsSelector, clusterHostGroupsSelector } from './selectors'
import { VMHAStatusForClusterList } from './VMHAStatusCell'

// FR-CHP-008 (PCD-11077): this list defaults to the backend's tenant-filtered cluster list; an
// admin can flip "View Clusters in All Tenants" to see every cluster regardless of tenant access
// (same pattern as Volumes/Networks/VMs list pages). The all-tenants list is fetched only when the
// toggle is on or the Create cluster modal is open (a manual Refresh also reloads hosts, whose
// all-tenants cluster dependency fetches it too), because Create needs the full cluster-name
// universe for duplicate-name validation (`existingClusterHostGroups`) — cluster names are
// unique across the whole system, not per tenant, so that check must never be scoped to the
// filtered list.
// `ModelDataKey` stays DataKeys.ClusterHostGroups because it only types `deleteAction` below;
// `deleteClusterHostGroup` removes the cluster from both the regular and all-tenants caches.
type ModelDataKey = DataKeys.ClusterHostGroups
type SelectorModel = ArrayElement<ReturnType<typeof clusterHostGroupsSelector>>
type ActionParams = InferActionParams<typeof listClusterHostGroups>

type Params = ActionParams & {}

const usePrefParams = createUsePrefParamsHook<Params & TablePrefsParams>(
  'ClusterHostGroups',
  listTablePrefs,
)

const defaultParams: Params & SortingState = {
  orderBy: 'created_at',
  orderDirection: 'desc',
}

// DRR Status Cell Component - Uses BadgeWithIcon (chip) for prominence
export const DRRStatusCell = ({ value }) => {
  return (
    <BadgeWithIcon
      variant={value ? 'success' : 'danger'}
      text={value ? 'Enabled' : 'Disabled'}
      icon={value ? 'check' : undefined}
      bold={true}
    />
  )
}

const modeString = {
  passthrough: 'Passthrough',
  vgpu: 'vGPU',
}

// GPU Status Cell Component - Uses subtle dot indicator with grey for disabled
export const GpuCellComponent = ({ value, item }) => {
  const classes = useStyles()
  return (
    <Text
      variant="body2"
      className={clsx(classes.gpuStatus, value ? classes.gpuEnabled : classes.gpuDisabled)}
    >
      {value ? `Enabled (${modeString[item?.gpu?.mode]})` : 'Disabled'}
    </Text>
  )
}

export const modeLabels = {
  custom: 'Custom',
  'host-model': 'Host Model',
  'host-passthrough': 'Host Passthrough',
}

export const matchesVMHAStatus = (value: string, searchString: string) =>
  String(value).toLocaleLowerCase() === searchString.toLocaleLowerCase()

const ClusterHostGroupsListPage = () => {
  const classes = useStyles()
  const [showEditClusterHostGroupModal, setShowEditClusterHostGroupModal] = useState<boolean>(false)
  const [selectedClusterHostGroup, setSelectedClusterHostGroup] = useState<SelectorModel[] | null>(
    null,
  )
  const [showDeleteClusterHostGroupModal, setShowDeleteClusterHostGroupModal] =
    useState<boolean>(false)
  const [showDownloadSupportBundleModal, setShowDownloadSupportBundleModal] =
    useState<boolean>(false)
  const [showAddHostModal, setShowAddHostModal] = useState<boolean>(false)
  const { params, getParamsUpdater } = usePrefParams(defaultParams)
  const urlFilters = useUrlFilterParams([
    { key: 'search', allowedValues: ['Protected', 'Not Protected', 'Degraded', 'Disabled'] },
  ])
  const initialSearchValue = urlFilters.search || undefined

  const dispatch = useDispatch()
  const persistedAllTenants = useSelector<RootState, boolean | undefined>(
    (state) => (state[sessionStoreKey] as SessionState)?.clustersAllTenants,
  )
  // FR-CHP-008 (PCD-11077): admin-only "View Clusters in All Tenants" toggle, same pattern as
  // Volumes/Networks/VMs list pages.
  const [allTenants, setAllTenants] = useState(persistedAllTenants ?? false)
  const handleAllTenantsToggle = useCallback(() => {
    const newValue = !allTenants
    setAllTenants(newValue)
    dispatch(sessionActions.updateSession({ clustersAllTenants: newValue }))
  }, [allTenants, dispatch])

  const {
    loading: loadingFiltered,
    reload: reloadFiltered,
    refreshing: refreshingFiltered,
  } = useListAction(listClusterHostGroups, { preRequisites: !allTenants })
  // The filtered list is only fetched while it is the displayed one, and the all-tenants list
  // only when displayed (toggle on) or when the Create modal needs it for its duplicate-name
  // validation, so a load costs a single /clusters round trip (FR-CHP-008).
  const { location } = useReactRouter()
  const isCreateOpen = location?.pathname === routeManager.routes.openstack.createCluster.path()
  const needAllTenants = allTenants || isCreateOpen
  const {
    loading: loadingAllTenants,
    reload: reloadAllTenants,
    refreshing: refreshingAllTenants,
  } = useListAction(listClusterHostGroupsAllTenants, { preRequisites: needAllTenants })
  const { reload: resmgrHostsReload } = useListAction(listResmgrHosts, { loadOnDemand: true })
  // The storage column counts a shared pool once, which depends on the blueprint.
  useListAction(listBlueprints)
  const filteredClusterHostGroups = useAppSelector(clusterHostGroupsSelector)
  const allTenantsClusterHostGroups = useAppSelector(clusterHostGroupsAllTenantsSelector)
  const clusterHostGroups = allTenants ? allTenantsClusterHostGroups : filteredClusterHostGroups
  const loading = allTenants ? loadingAllTenants : loadingFiltered
  const clusterHostGroupsRefreshing = allTenants ? refreshingAllTenants : refreshingFiltered
  const reload = useCallback(
    (...args: Parameters<typeof reloadFiltered>) => {
      // Reload both lists after any create/edit/delete/refresh: a cluster's tenant access decides
      // which list(s) it belongs in, and only the backend knows the current answer.
      reloadFiltered(...args)
      reloadAllTenants(...args)
    },
    [reloadFiltered, reloadAllTenants],
  )
  const { loading: loadingClusterVMHAStatus, reload: loadClusterVMHAStatus } = useListAction(
    listClusterVMHAStatus,
    {
      loadOnDemand: true,
      params: { clusters: clusterHostGroups },
    },
  )

  const { reload: hostAggregatesReload } = useListAction(listHostAggregates, {
    params,
  })
  const hostAggregates = useAppSelector(hostAggregatesSelector)

  // Load Cluster VMHA Status whenever the set of displayed clusters changes (first load, or the
  // all-tenants toggle adding tenant-dedicated clusters) — not on every poll refresh.
  const vmhaFetchedForRef = useRef('')
  useEffect(() => {
    if (!clusterHostGroups?.length || loading) {
      return
    }

    const clusterNamesKey = clusterHostGroups
      .map((cluster) => cluster.name)
      .sort()
      .join('|')
    if (vmhaFetchedForRef.current === clusterNamesKey) {
      return
    }

    vmhaFetchedForRef.current = clusterNamesKey
    // FR-VSC-002: Use refetch=true to bypass stale cached params from initial render
    loadClusterVMHAStatus(true, true)
  }, [clusterHostGroups, loading])

  const columns: GridViewColumn<SelectorModel>[] = useMemo(
    () => [
      {
        key: 'name',
        label: 'Name',
        CellComponent: ({ value, item }) => (
          <SimpleLink src={routeManager.routes.openstack.clusterDetails.path({ id: item?.name })}>
            {(<>{value}</>) as unknown as React.ReactNode}
          </SimpleLink>
        ),
      },
      {
        key: 'stretchedClusters',
        label: (
          <TitleWithInfoIcon
            titleText="Failure Domains"
            tooltipMsg={
              'Set when a cluster is stretched across two sites. Clusters that are not stretched show a dash.'
            }
          />
        ),
        disableSorting: true,
        CellComponent: StretchedClustersCellComponent,
      },
      {
        key: 'hostlist.length',
        label: 'Hosts',
        CellComponent: ({ value = '0' }) => <Text variant="body2">{value as string}</Text>,
      },
      {
        key: 'physicalNetworkLabels',
        label: 'Physical Network Label',
        display: false,
        CellComponent: (props) => {
          const physicalNetworkLabels = (props?.value as string[]) || []
          return <Text variant="body2"> {physicalNetworkLabels?.join(', ')}</Text>
        },
      },
      {
        key: 'vmhaStatus',
        label: 'VMHA',
        CellComponent: ({ value, item }) => (
          <VMHAStatusForClusterList
            value={(value || '') as string}
            item={item}
            loading={loadingClusterVMHAStatus}
          />
        ),
      },
      {
        key: 'autoResourceRebalancing.enabled',
        label: 'Dynamic Resource Rebalancing',
        CellComponent: DRRStatusCell,
      },
      {
        key: 'gpu.enabled',
        label: 'GPU',
        CellComponent: GpuCellComponent,
      },
      {
        key: 'access',
        label: 'Tenant Access',
        disableSorting: true,
        CellComponent: ({ item }) => (
          <ClusterAccessCell item={item} containerType="panel" maxBadgesVisible={1} />
        ),
      },
      {
        key: 'cpu.mode',
        label: 'Virtualized Guest CPU Mode',
        render: (value: any) => {
          return modeLabels[value] || 'Default'
        },
        display: false,
      },
      {
        key: 'cpu.model',
        label: 'Virtualized Guest CPU Model',
        display: false,
      },
      {
        key: 'totalUsage.cpuUsage',
        label: 'CPU Usage (vCPU)',
        CellComponent: UsageProgress,
        sortFn: usagePercentSort,
      },
      {
        key: 'totalUsage.memoryUsage',
        label: 'Memory Usage (GiB)',
        CellComponent: UsageProgress,
        sortFn: usagePercentSort,
      },
      {
        key: 'totalUsage.storageUsage',
        label: 'Ephemeral Storage Usage (GiB)',
        CellComponent: UsageProgress,
        sortFn: usagePercentSort,
      },
      {
        key: 'created_at',
        label: 'Created',
        render: (time: string) => formatTimestampToLocal(time),
        display: false,
      },
    ],
    [loadingClusterVMHAStatus],
  )

  const batchActions = useMemo(
    () => [
      {
        cond: (selectedItems) => selectedItems.length === 1,
        disabledTooltip: 'You can only edit one cluster at a time',
        label: 'Edit Cluster',
        icon: 'edit',
        handleAction: (selectedItems) => {
          setSelectedClusterHostGroup(selectedItems)
          setShowEditClusterHostGroupModal(true)
        },
        refreshAfterSuccess: false,
      },
      {
        label: 'Generate Support Bundle',
        icon: 'block-question',
        handleAction: (selectedItems) => {
          setSelectedClusterHostGroup(selectedItems)
          setShowDownloadSupportBundleModal(true)
        },
        refreshAfterSuccess: false,
        disabledTooltip: 'You can only download support bundle for one cluster at a time',
      },
      {
        cond: (selectedItems) => selectedItems.length === 1,
        disabledTooltip: 'You can only add hosts to one cluster at a time',
        label: 'Add Host',
        icon: 'plus',
        handleAction: (selectedItems) => {
          setSelectedClusterHostGroup(selectedItems)
          setShowAddHostModal(true)
        },
        refreshAfterSuccess: false,
      },
    ],
    [],
  )

  const handlePolling = useCallback(
    () =>
      Promise.resolve().then(() => {
        reload(true, false)
      }),
    [reload],
  )

  const handleReload = useCallback(() => {
    reload(true, true)
    hostAggregatesReload(true, true)
    resmgrHostsReload(true, true)
    loadClusterVMHAStatus(true, true)
  }, [reload, hostAggregatesReload, resmgrHostsReload, loadClusterVMHAStatus])

  return (
    <>
      <DocumentMeta title="Clusters" />
      <PollingData
        hidden
        loading={loading}
        onReload={handlePolling}
        refreshDuration={POLLING_INTERVALS.THIRTY_SECONDS}
      />
      <CreateClusterHostGroupsModal
        addRoute={routeManager.routes.openstack.createCluster}
        existingClusterHostGroups={allTenantsClusterHostGroups}
        existingHostAggregates={hostAggregates}
        reload={handleReload}
      />
      {showEditClusterHostGroupModal && (
        <EditClusterHostGroupModal
          onClose={(reload) => {
            setShowEditClusterHostGroupModal(false)
            if (reload) handleReload()
          }}
          rows={selectedClusterHostGroup}
        />
      )}
      {showDeleteClusterHostGroupModal && (
        <DeleteClusterHostGroupsDialog
          onClose={() => setShowDeleteClusterHostGroupModal(false)}
          rows={selectedClusterHostGroup}
        />
      )}
      {showDownloadSupportBundleModal && (
        <GenerateSupportBundleModal
          onClose={() => setShowDownloadSupportBundleModal(false)}
          rows={selectedClusterHostGroup}
        />
      )}
      {showAddHostModal && selectedClusterHostGroup?.[0] && (
        <AddHostToClusterModal
          selectedCluster={selectedClusterHostGroup[0]}
          onClose={() => setShowAddHostModal(false)}
          reload={handleReload}
        />
      )}
      <ListContainer<ModelDataKey, SelectorModel>
        searchTargets={['name', 'vmhaStatus']}
        searchFunctions={[{ property: 'vmhaStatus', searchFn: matchesVMHAStatus }]}
        uniqueIdentifier="name"
        loading={loading}
        loadingMessage="Loading Clusters..."
        onRefresh={handleReload}
        deleteAction={deleteClusterHostGroup}
        deleteCond={(clusterHostGroups) =>
          !clusterHostGroups.some((clusterHostGroup) => clusterHostGroup?.hostlist?.length > 0)
        }
        DeleteDialogComponent={DeleteClusterHostGroupsDialog}
        deleteDisabledInfo="You cannot delete clusters which have hosts assigned to them"
        postDeleteAction={handleReload}
        refreshing={clusterHostGroupsRefreshing}
        multiSelection
        data={clusterHostGroups}
        columns={columns}
        getParamsUpdater={getParamsUpdater}
        label="Clusters"
        showItemsCountInLabel
        extraHeaderContent={
          canManageInfrastructure() && (
            <ToggleSwitch
              active={allTenants}
              className={classes.toggleSwitch}
              onClick={handleAllTenantsToggle}
              label="View Clusters in All Tenants"
            />
          )
        }
        batchActions={batchActions}
        emptyContent="No clusters found"
        addUrl={routeManager.routes.openstack.createCluster.path()}
        addText="Add Cluster"
        initialSearchValue={initialSearchValue}
        {...pick(listTablePrefs, params)}
      />
    </>
  )
}

export default ClusterHostGroupsListPage

const useStyles = makeStyles<Theme>((theme: Theme) => {
  const commonStyles = {
    display: 'inline-grid',
    alignItems: 'center',
    gridTemplateColumns: 'minmax(22px, max-content) 1fr',
    whiteSpace: 'nowrap' as const,
  }

  return {
    gpuStatus: {
      ...commonStyles,
      '&:before': {
        content: '""',
        display: 'inherit',
        height: 8,
        width: 8,
        borderRadius: '50%',
      },
    },
    gpuEnabled: {
      '&:before': {
        backgroundColor: theme.palette.green.main,
      },
    },
    gpuDisabled: {
      '&:before': {
        backgroundColor: theme.palette.grey[500],
      },
    },
    toggleSwitch: {
      marginBottom: '8px',
    },
  }
})
